import { styled } from "@pigment-css/react";
import type { Overview as OverviewSchema } from "@acto/genui-components-schema";
import { Card, Header, CardHeadline, MetaLabel } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { SentimentColor } from "./SentimentColor";

// The 30% floor caps the grid at 3 columns on wide containers; the 140px floor
// keeps 2 columns on the narrow taskpane.
const ItemGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(max(140px, 30%), 1fr))",
  gap: theme.fieldSalesTwo.spacing[16],
}));

// Subgrid shares the label and value rows across the grid, so values stay on
// one baseline even when a neighboring label wraps. rowGap overrides the
// inherited 16px gutter between the two shared rows.
const Item = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "subgrid",
  gridRow: "span 2",
  rowGap: theme.fieldSalesTwo.spacing[6],
  minWidth: 0,
}));

const ValueRow = styled("p")(({ theme }) => ({
  margin: 0,
  display: "flex",
  alignItems: "baseline",
  flexWrap: "wrap",
  columnGap: theme.fieldSalesTwo.spacing[6],
}));

const ValueText = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "anywhere",
}));

const SubValueText = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[700],
  overflowWrap: "anywhere",
}));

interface OverviewProps {
  part: OverviewSchema;
}

export function Overview({ part }: OverviewProps) {
  return (
    <Card>
      <Header>
        {part.label && <MetaLabel>{part.label}</MetaLabel>}
        <CardHeadline>{part.headline}</CardHeadline>
      </Header>
      {part.items.length > 0 && (
        <>
          <Divider />
          <ItemGrid>
            {part.items.map((item, index) => (
              <Item key={index}>
                <MetaLabel>{item.label}</MetaLabel>
                <ValueRow>
                  <ValueText>
                    <SentimentColor sentiment={item.value.sentiment ?? "neutral"}>{item.value.text}</SentimentColor>
                  </ValueText>
                  {item.subValue && (
                    <SubValueText>
                      <SentimentColor sentiment={item.subValue.sentiment ?? "neutral"}>
                        {item.subValue.text}
                      </SentimentColor>
                    </SubValueText>
                  )}
                </ValueRow>
              </Item>
            ))}
          </ItemGrid>
        </>
      )}
    </Card>
  );
}
